// Typed tracking-plan SDK (github.com/AsthaBhopal/analytics_schema) wired to
// the existing RudderStack transport.
//
// Every event goes through the generated, compiler-checked methods on
// `analytics` — one method per event, enums for every governed value, stamped
// with schema_version. A payload carries only the properties its event
// declares and the caller set; the global context below is the exception, so
// its unset fields go out as null on every event. There is no other way to
// emit an event.
//
// Adding an event or enum value = PR to the analytics_schema repo (one-line
// proto edit; codegen + release are automated), then bump the package here.
import { AccountType, RupeezyAnalytics } from "@asthabhopal/tracking-plan";
import type { GlobalContext } from "@asthabhopal/tracking-plan";
import type { Store } from "@reduxjs/toolkit";
import { currentDeviceType, rudderIdentify, rudderTrack } from "./rudder";
import store from "../store";

// Which Rupeezy app emitted the event (all apps share one RudderStack source +
// domain, so events are otherwise indistinguishable) — same value rudder.ts
// stamps on the RudderStack `context.app` field.
const APP_NAME = "rupeezy_login";
// Build commit — injected by buildspec.yaml as REACT_APP_COMMIT_VERSION.
const APP_VERSION = process.env.REACT_APP_COMMIT_VERSION || "";

// Account kinds as the auth backend names them (constants.AccountType in
// AsthaBhopal/authentication, derived from the PAN's 4th character), keyed
// lower-case because the backend compares them case-insensitively. Minor,
// AOP, Trust, BOI, Local Authority, Government and Judicial have no
// AccountType and are left unset rather than guessed.
const ACCOUNT_TYPES: Record<string, AccountType> = {
  individual: AccountType.INDIVIDUAL,
  huf: AccountType.HUF,
  firm: AccountType.FIRM,
  corporate: AccountType.COMPANY,
};

const warnedAccountTypes = new Set<string>();

/** Backend account type -> AccountType; undefined, with a one-time warning, when it has no match. */
export function toAccountType(raw: string | null | undefined): AccountType | undefined {
  const key = raw?.trim().toLowerCase();
  if (!key) return undefined;
  const type = ACCOUNT_TYPES[key];
  if (type === undefined && !warnedAccountTypes.has(key)) {
    warnedAccountTypes.add(key);
    // eslint-disable-next-line no-console
    console.warn(`[analytics] account type "${raw}" has no AccountType — account_type left unset.`);
  }
  return type;
}

// The redux state is loosely typed at this boundary; narrow the paths we read.
type AuthSnapshot = {
  authUser?: {
    data?: { applicationId?: string };
    activeAccount?: { clientCode?: string; accountType?: string } | null;
  };
};

/** The global context for a store state: this app, plus the account being operated. */
function contextFor(state: unknown): GlobalContext {
  const auth = (state as AuthSnapshot | undefined)?.authUser;
  const account = auth?.activeAccount;
  return {
    appName: APP_NAME,
    deviceType: currentDeviceType(),
    appVersion: APP_VERSION,
    applicationId: auth?.data?.applicationId,
    clientCode: account?.clientCode || undefined,
    accountType: toAccountType(account?.accountType),
  };
}

// This module sits in an import cycle (store → authReducer → trackingPlan →
// store), so `store` may not be initialised yet when the module-level
// `analytics` construction below runs — hence the try/catch.
function currentContext(): GlobalContext {
  try {
    return contextFor(store.getState());
  } catch {
    return contextFor(undefined);
  }
}

/** Typed tracking surface — one generated method per event in the plan. */
export const analytics = new RupeezyAnalytics({
  track: rudderTrack,
  context: currentContext(),
});

/**
 * Binds the current user to the analytics session (RudderStack identify).
 * The id and traits belong to the person: never put the account on them.
 * Logout resets identity via rudderReset() (auth slices, axios 401 handler).
 */
export function identify(userId: string, traits: Record<string, unknown> = {}): void {
  if (!userId) return;
  rudderIdentify(userId, traits);
}

/**
 * Keeps `target`'s global context equal to the store's, the one place the
 * context is written: screens change the store (setUser, setActiveAccount,
 * logout), never the context.
 *
 * Moving from one opened account to another is a switch: the context moves
 * first, then account_switched fires, so the event and everything after it
 * belong to the account switched into. Choosing an account when none was
 * chosen (straight after sign-in) is not a switch, and neither is starting to
 * open one, which has no code yet.
 */
export function syncContextWithStore(
  source: Pick<Store, "getState" | "subscribe">,
  target: RupeezyAnalytics,
): () => void {
  let current = contextFor(source.getState());
  target.updateContext(current);
  return source.subscribe(() => {
    const next = contextFor(source.getState());
    if (
      next.applicationId === current.applicationId &&
      next.clientCode === current.clientCode &&
      next.accountType === current.accountType
    ) {
      return;
    }
    const from = current;
    current = next;
    target.updateContext(next);

    if (!from.clientCode || !next.clientCode || from.clientCode === next.clientCode) return;
    if (from.accountType === undefined || next.accountType === undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        `[analytics] account_switched ${from.clientCode} -> ${next.clientCode} not sent: account type unknown.`,
      );
      return;
    }
    target.accountSwitched({
      fromAccountType: from.accountType,
      fromClientCode: from.clientCode,
      toClientCode: next.clientCode,
      toAccountType: next.accountType,
    });
  });
}

// Deferred a microtask so the store module has finished initialising (import
// cycle above).
if (typeof window !== "undefined") {
  queueMicrotask(() => syncContextWithStore(store, analytics));
}

// String -> enum conversion lives in the SDK as generated per-enum parsers
// (sourceFromWire, screenFromWire, ...) — import them from
// @asthabhopal/tracking-plan directly. The app-side exceptions are
// toAccountType above, for the backend's own account-type names, and the
// free-form label munging below, which the parsers deliberately don't do.

/** Snake-cases free-form label text ("Trending News" -> "trending_news"). */
export function snake(v: unknown): string | undefined {
  const raw = String(v ?? "").trim();
  if (!raw) return undefined;
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2") // camelCase -> camel_Case
    .replace(/[\s.,/-]+/g, "_")
    .toLowerCase()
    .replace(/_(tap|tapped|click|clicked|view|viewed|launch|btn|button)$/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}
